// ============================================================================
// feedCardProps — les props de lecture de PublicationCard, en UN seul endroit
// ============================================================================
// Le fil (FeedCard d'AccueilView) et l'apercu telephone de l'editeur rendent le
// MEME composant avec les MEMES props : ce que l'on voit en ecrivant est ce qui
// paraitra dans le fil. Les deux appelants passent par cette fonction — jamais
// une copie des libelles, des reactions ou du format de date. Les props
// interactives (onReact, commentaires, image resolue) restent a l'appelant.

import type { PublicationCardProps } from '@gs/ui';
import type { TFunction } from 'i18next';
import type { Publication } from '../../hooks/useAccueil';
import { formatRelative } from './format';

/** Ce qu'il faut d'une publication pour la rendre en lecture. */
export type FeedCardSource = Pick<
  Publication,
  | 'title' | 'body' | 'category' | 'authorName' | 'authorEmail' | 'postedAt'
  | 'reactions' | 'myReaction' | 'viewCount' | 'commentCount'
>;

export type FeedCardReadProps = Omit<
  PublicationCardProps,
  'imageObjectUrl' | 'onReact' | 'onToggleComments' | 'commentsExpanded' | 'children' | 'className'
>;

export function feedCardProps(
  p: FeedCardSource,
  locale: string,
  t: TFunction,
  now: Date = new Date(),
): FeedCardReadProps {
  return {
    authorName: p.authorName ?? p.authorEmail,
    postedAtLabel: formatRelative(p.postedAt, locale, now),
    categoryLabel: t(`gest:accueil.categories.${p.category}`),
    title: p.title,
    body: p.body,
    imageAlt: t('gest:accueil.feed.imageAlt', { title: p.title }),
    readMoreLabel: t('gest:accueil.feed.readMore'),
    readLessLabel: t('gest:accueil.feed.readLess'),
    reactions: [
      { key: 'like', emoji: '👍', label: t('gest:accueil.reactions.like'), count: p.reactions.like, active: p.myReaction === 'like' },
      { key: 'love', emoji: '❤️', label: t('gest:accueil.reactions.love'), count: p.reactions.love, active: p.myReaction === 'love' },
      { key: 'celebrate', emoji: '🎉', label: t('gest:accueil.reactions.celebrate'), count: p.reactions.celebrate, active: p.myReaction === 'celebrate' },
    ],
    viewsLabel: p.viewCount > 0 ? t('gest:accueil.feed.viewedBy', { count: p.viewCount }) : undefined,
    commentsLabel:
      p.commentCount > 0
        ? t('gest:accueil.feed.comments', { count: p.commentCount })
        : t('gest:accueil.feed.commentCta'),
  };
}
